import { BasePage } from './bases/BasePage';
import { BreadcrumbSubBar } from '@components/BreadcrumbSubBar';
import { ManageForm } from '@components/forms/ManageForm';
import {
    Grid,
    CircularProgress,
    Typography,
    Tabs,
    Tab,
    Paper,
    Box,
    Button,
    Chip,
    Link,
    IconButton,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFetch } from '@hooks/useFetch';
import { getPendingPlanChanges, updatePlanStatus } from '@api/system';
import { getRecentBillingChanges } from '@api/system';
import { CUT_FEE_MAP } from '@consts/company';
import { useAlert } from '@hooks/useAlert';
import { AppRoutes } from '@consts/routes';

import type { RootState } from '@features/redux/store';
import { LoadingButton } from '@mui/lab';
import { RecentActivityCard } from '@components/billing/RecentActivityCard';
import { RequestCard } from '@components/billing/RequestCard';
import {
    type PlanChangeRequest,
    type PlanChangeResponse,
    type PlanChangeStatus,
    type RecentPlanChange,
} from '@/types/billing';

dayjs.extend(utc);

export const BillingChanges = () => {
    const navigate = useNavigate();
    const { setAlert } = useAlert();
    const {
        IdToken,
        Roles: { role },
    } = useSelector((state: RootState) => state.auth);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const carouselRef = useRef<HTMLDivElement>(null);

    // Group approved requests for tabs
    const today = dayjs();
    const [tab, setTab] = useState(0);

    // Finance-only guard – redirect non-finance users away from this page.
    useEffect(() => {
        if (!role.type.finance) navigate(AppRoutes.Insights);
    }, [role.type.finance, navigate]);

    const {
        data: { Pending, Approved },
        loading,
        refetch,
    } = useFetch<PlanChangeResponse, void>({
        fetcher: () => getPendingPlanChanges(IdToken!),
        defaultValue: { Pending: [], Approved: [] },
        spinner: false,
    });

    const { data: recentChanges, loading: recentLoading } = useFetch<RecentPlanChange[], void>({
        fetcher: () => getRecentBillingChanges(IdToken!),
        defaultValue: [],
        spinner: false,
    });

    // Sort recent changes once for reuse
    const sortedRecentChanges = [...recentChanges].sort(
        (a, b) => dayjs.utc(b.LastStatusUpdate).valueOf() - dayjs.utc(a.LastStatusUpdate).valueOf()
    );

    const handleAction = async (planId: number, status: Extract<PlanChangeStatus, 'Approved' | 'Rejected'>) => {
        const resp = await updatePlanStatus(IdToken!, planId, status);
        if (resp.success) {
            setAlert(`Successfully ${status.toLowerCase()} plan change`, 'success');
            refetch();
        } else {
            setAlert(resp.message, 'error');
        }
    };

    const approvedThisWeek = Approved.filter((r) => dayjs.utc(r.EffectiveDate).isSame(today, 'week'));

    return (
        <BasePage>
            <BreadcrumbSubBar />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <ManageForm title='Billing Changes' size={12}>
                    {loading ? (
                        <Grid item xs={12} sm={12} md={12} display='flex' justifyContent='center'>
                            <CircularProgress size='3rem' />
                        </Grid>
                    ) : (
                        <>
                            {/* Pending Requests */}
                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                <Paper
                                    sx={{
                                        p: 2,
                                        border: '1px solid #e0e0e0',
                                        maxHeight: '500px',
                                        overflowY: 'auto',
                                    }}
                                    elevation={1}>
                                    <Typography variant='h6' fontWeight={700} mb={2}>
                                        Pending Approval Requests
                                    </Typography>
                                    {Pending.length ? (
                                        Pending.map((request: PlanChangeRequest) => (
                                            <RequestCard
                                                key={request.PlanID}
                                                request={request}
                                                handleAction={handleAction}
                                                actions={true}
                                            />
                                        ))
                                    ) : (
                                        <Typography>No pending requests.</Typography>
                                    )}
                                </Paper>
                            </Grid>

                            {/* Scheduled Changes */}
                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                <Paper
                                    sx={{
                                        p: 2,
                                        border: '1px solid #e0e0e0',
                                        maxHeight: '500px',
                                        overflowY: 'auto',
                                    }}
                                    elevation={1}>
                                    <Typography variant='h6' fontWeight={700} mb={2}>
                                        Scheduled Billing Changes
                                    </Typography>
                                    <Tabs
                                        value={tab}
                                        onChange={(_, v) => setTab(v)}
                                        sx={{
                                            mb: 1,
                                            backgroundColor: '#f5f5f5',
                                            borderRadius: 2,
                                            minHeight: 40,
                                            width: 'fit-content',
                                            '& .MuiTabs-indicator': { display: 'none' },
                                        }}>
                                        <Tab
                                            label={`This Week (${approvedThisWeek.length})`}
                                            sx={{
                                                textTransform: 'none',
                                                minHeight: 32,
                                                px: 2,
                                                borderRadius: 1,
                                                mx: 0.5,
                                                '&.Mui-selected': {
                                                    backgroundColor: 'primary.main',
                                                    color: 'primary.contrastText',
                                                },
                                            }}
                                        />
                                        <Tab
                                            label={`All Scheduled (${Approved.length})`}
                                            sx={{
                                                textTransform: 'none',
                                                minHeight: 32,
                                                px: 2,
                                                borderRadius: 1,
                                                mx: 0.5,
                                                '&.Mui-selected': {
                                                    backgroundColor: 'primary.main',
                                                    color: 'primary.contrastText',
                                                },
                                            }}
                                        />
                                    </Tabs>

                                    {tab === 0 &&
                                        (approvedThisWeek.length === 0 ? (
                                            <Typography>No scheduled changes.</Typography>
                                        ) : (
                                            <Box mt={1}>
                                                {approvedThisWeek.map((request) => (
                                                    <RequestCard
                                                        key={`${request.PlanID}-approved-this-week`}
                                                        request={request}
                                                        handleAction={handleAction}
                                                        actions={false}
                                                    />
                                                ))}
                                            </Box>
                                        ))}
                                    {tab === 1 &&
                                        (Approved.length === 0 ? (
                                            <Typography>No scheduled changes.</Typography>
                                        ) : (
                                            <Box mt={1}>
                                                {Approved.map((request) => (
                                                    <RequestCard
                                                        key={`${request.PlanID}-approved-all-time`}
                                                        request={request}
                                                        handleAction={handleAction}
                                                        actions={false}
                                                    />
                                                ))}
                                            </Box>
                                        ))}
                                </Paper>
                            </Grid>

                            {/* Recent Activity */}
                            <Grid item xs={12}>
                                <Paper sx={{ p: 2, border: '1px solid #e0e0e0', overflowY: 'auto' }} elevation={1}>
                                    <Typography variant='h6' fontWeight={700} mb={2}>
                                        Recent Activity
                                    </Typography>
                                    {recentLoading ? (
                                        <Box display='flex' justifyContent='center' my={3}>
                                            <CircularProgress size='2rem' />
                                        </Box>
                                    ) : sortedRecentChanges.length ? (
                                        isMobile ? (
                                            sortedRecentChanges.map((recentChange) => (
                                                <RecentActivityCard key={recentChange.PlanID} change={recentChange} />
                                            ))
                                        ) : (
                                            <Box position='relative'>
                                                <IconButton
                                                    size='small'
                                                    onClick={() =>
                                                        carouselRef.current?.scrollBy({
                                                            left: -300,
                                                            behavior: 'smooth',
                                                        })
                                                    }
                                                    sx={{
                                                        position: 'absolute',
                                                        left: 0,
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        zIndex: 1,
                                                    }}>
                                                    <ChevronLeft />
                                                </IconButton>
                                                <Box
                                                    ref={carouselRef}
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 2,
                                                        overflowX: 'auto',
                                                        scrollBehavior: 'smooth',
                                                        p: 1,
                                                        mx: 4,
                                                    }}>
                                                    {sortedRecentChanges.map((recentChange) => (
                                                        <RecentActivityCard
                                                            key={recentChange.PlanID}
                                                            change={recentChange}
                                                        />
                                                    ))}
                                                </Box>
                                                <IconButton
                                                    size='small'
                                                    onClick={() =>
                                                        carouselRef.current?.scrollBy({ left: 300, behavior: 'smooth' })
                                                    }
                                                    sx={{
                                                        position: 'absolute',
                                                        right: 0,
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                    }}>
                                                    <ChevronRight />
                                                </IconButton>
                                            </Box>
                                        )
                                    ) : (
                                        <Typography>No recent activity.</Typography>
                                    )}
                                </Paper>
                            </Grid>
                        </>
                    )}
                </ManageForm>
            </div>
        </BasePage>
    );
};

export default BillingChanges;
